import type { SupabaseClient } from '../../lib/supabase';
import type { Json } from '../../types/database.types';
import type { SavedView, SavedViewInput } from './types';
import { parseSavedView, SAVED_VIEW_POSITION_GAP } from './types';

/**
 * Database access for saved views. Row Level Security scopes every statement
 * to the signed-in person's own rows: saved views are personal (handoff 26)
 * and there is no sharing path in either direction.
 */

function fail(message: string): never {
  throw new Error(message);
}

// prettier-ignore
const SAVED_VIEW_COLUMNS = 'id, owner_id, project_id, name, view_mode, filters, sort, group_by, visible_columns, position, created_at, updated_at';

/** Every saved view the person owns, in their chosen order. */
export async function fetchSavedViews(client: SupabaseClient): Promise<SavedView[]> {
  const { data, error } = await client
    .from('saved_views')
    .select(SAVED_VIEW_COLUMNS)
    .order('position', { ascending: true })
    .order('created_at', { ascending: true });

  if (error) {
    fail(error.message);
  }
  return (data ?? []).map(parseSavedView);
}

export async function createSavedView(
  client: SupabaseClient,
  input: SavedViewInput & { ownerId: string; position: number },
): Promise<SavedView> {
  const { data, error } = await client
    .from('saved_views')
    .insert({
      owner_id: input.ownerId,
      project_id: input.projectId,
      name: input.name.trim(),
      view_mode: input.viewMode,
      // The typed shapes are plain JSON objects; the cast records that.
      filters: input.filters as Json,
      sort: input.sort as Json,
      group_by: input.groupBy,
      visible_columns: input.visibleColumns,
      position: input.position,
    })
    .select(SAVED_VIEW_COLUMNS)
    .single();

  if (error) {
    fail(error.message);
  }
  return parseSavedView(data);
}

/**
 * Updating a view means re-saving its arrangement. Owner and project are
 * frozen by the database; only the granted columns are sent.
 */
export async function updateSavedView(
  client: SupabaseClient,
  viewId: string,
  input: Omit<SavedViewInput, 'projectId'>,
): Promise<SavedView> {
  const { data, error } = await client
    .from('saved_views')
    .update({
      name: input.name.trim(),
      view_mode: input.viewMode,
      filters: input.filters as Json,
      sort: input.sort as Json,
      group_by: input.groupBy,
      visible_columns: input.visibleColumns,
    })
    .eq('id', viewId)
    .select(SAVED_VIEW_COLUMNS)
    .single();

  if (error) {
    fail(error.message);
  }
  return parseSavedView(data);
}

export async function deleteSavedView(client: SupabaseClient, viewId: string): Promise<void> {
  const { error } = await client.from('saved_views').delete().eq('id', viewId);
  if (error) {
    fail(error.message);
  }
}

/** Where a new view lands: after the last one the person has. */
export function nextSavedViewPosition(views: SavedView[]): number {
  const highest = views.reduce((max, view) => Math.max(max, view.position), 0);
  return highest + SAVED_VIEW_POSITION_GAP;
}

/**
 * The one refusal a person will actually meet: the per-person, per-project
 * unique name. It arrives as a unique-constraint violation naming one of the
 * two partial indexes.
 */
export function savedViewErrorMessage(message: string): string {
  if (
    message.includes('saved_views_owner_project_name_idx') ||
    message.includes('saved_views_owner_global_name_idx')
  ) {
    return 'You already have a view with that name here. Pick another.';
  }
  return 'That view could not be saved. Try again.';
}
